import { makeAutoObservable } from 'mobx'
import { HandoffNotifySettingsStore } from 'entities/AIAgent/ui/HandoffNotifySettings/store/HandoffNotifySettingsStore.ts'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import type { ChatStore } from 'pages/chatbot/ui/UnifiedAgent/ui/Chat/store/chatStore.ts'
import {
  formatWidgetMessage,
  type IWidgetQAPair,
} from 'pages/chatbot/ui/UnifiedAgent/lib/formatWidgetMessage.ts'
import { type InstructionsSectionStore } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/store/instructionsSectionStore.tsx'

const stepsTitleMap = {
  notifyStep: 'Notify team members about the handoff?',
  whoNotified: 'Who should be notified about the handoff?',
  trigger: 'When should this action be triggered?',
}

type IStepKey = keyof typeof stepsTitleMap

const stepsMap = {
  withoutSettings: ['notifyStep', 'trigger'] satisfies IStepKey[],
  withSettings: ['notifyStep', 'whoNotified', 'trigger'] satisfies IStepKey[],
}

export class HandOffActionWidgetStore {
  readonly handoffNotifySettingsStore: HandoffNotifySettingsStore

  constructor(
    readonly entities: IEntitiesStore,
    readonly chatStore: ChatStore,
    readonly sectionStore: InstructionsSectionStore,
    readonly parent: {
      offsetStep: number
      onBack: () => void
      pairs: IWidgetQAPair[]
      handleClose: () => void
    }
  ) {
    this.handoffNotifySettingsStore = new HandoffNotifySettingsStore(
      entities,
      sectionStore.getHandoffNotifySettings() || {
        notify_enabled: true,
      }
    )
    makeAutoObservable(this)
  }

  currentStepKey: IStepKey = 'notifyStep'
  stepKeys: IStepKey[] = stepsMap['withoutSettings']

  get isNotifyStep() {
    return this.currentStepKey === 'notifyStep'
  }

  get isWhoNotifiedStep() {
    return this.currentStepKey === 'whoNotified'
  }

  get isTriggerStep() {
    return this.currentStepKey === 'trigger'
  }

  get step() {
    return this.stepKeys.indexOf(this.currentStepKey) + 1 + this.parent.offsetStep
  }

  get totalSteps() {
    return this.stepKeys.length + this.parent.offsetStep
  }

  get isFirstStep() {
    return this.currentStepKey === this.stepKeys[0]
  }

  get currentHeader() {
    return stepsTitleMap[this.currentStepKey]
  }

  get isNextStep() {
    return this.currentStepKey === 'whoNotified'
  }

  get isNextDisabled() {
    if (this.isWhoNotifiedStep) {
      const notifySettings = this.handoffNotifySettingsStore.notifySettings
      return !(
        notifySettings.conversation_assignee ||
        notifySettings.contact_owner ||
        notifySettings.all_inbox_members ||
        notifySettings.additional_users?.length
      )
    }
    return false
  }

  onNext = () => {
    if (this.isNextDisabled) return
    const nextIndex = this.stepKeys.indexOf(this.currentStepKey) + 1
    this.currentStepKey = this.stepKeys[nextIndex]
  }

  get stringWhoNotified(): string {
    const values: string[] = []
    if (this.handoffNotifySettingsStore.notifyConversationAssignee) {
      values.push('Conversations assignee')
    }
    if (this.handoffNotifySettingsStore.notifyContactOwner) {
      values.push('Contact owner')
    }
    if (this.handoffNotifySettingsStore.notifyAllInboxMembers) {
      values.push('All inbox members')
    }
    this.handoffNotifySettingsStore.multiSelectMembersStore.selectedMembers.forEach((item) => {
      if (item.data?.name) {
        values.push(item.data?.name)
      }
    })
    return values.join(', ')
  }

  get isSubmitStep() {
    return this.currentStepKey === 'trigger'
  }

  get isSubmitDisabled() {
    return !this.triggerText.trim()
  }
  onSubmit = () => {
    if (this.isSubmitDisabled) return
    const pairs = [...this.parent.pairs]
    pairs.push({ q: stepsTitleMap.notifyStep, a: this.isNotifyEnabled ? 'Yes' : 'No' })
    if (this.isNotifyEnabled) {
      pairs.push({ q: stepsTitleMap.whoNotified, a: this.stringWhoNotified })
    }
    pairs.push({ q: stepsTitleMap.trigger, a: this.triggerText.trim() })
    this.sectionStore.onUpdateHandoffToHumanAction(this.handoffNotifySettingsStore.notifySettings)
    const message = formatWidgetMessage(pairs)
    this.chatStore.sendMessage(message)
  }

  handleClose = () => {
    this.parent.handleClose()
  }
  goBack = () => {
    if (this.isFirstStep) {
      this.parent.onBack()
    } else {
      const prevIndex = this.stepKeys.indexOf(this.currentStepKey) - 1
      this.currentStepKey = this.stepKeys[prevIndex]
    }
  }

  hint: string[] = []
  setHint = (hint: string[]) => {
    this.hint = hint
  }

  get isNotifyEnabled() {
    return this.handoffNotifySettingsStore.isNotifyTeamMembers
  }

  onSelectNotify = (value: boolean) => {
    if (value) {
      this.stepKeys = stepsMap['withSettings']
    } else {
      this.stepKeys = stepsMap['withoutSettings']
    }
    this.handoffNotifySettingsStore.setNotifyTeamMembers(value)
    this.onNext()
  }

  triggerText = ''
  setTriggerText = (value: string) => {
    this.triggerText = value
  }
}
